import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import {useAuth} from '../context/AuthContext';
import {SubscriptionType} from '../models/enums/SubscriptionType';
import PriceInfo from "../models/PriceInfo";
import SubscriptionSummary from "../models/SubscriptionSummary";
import {StripeSubscriptionStatus} from "../models/enums/StripeSubscriptionStatus";
import {createStripePortalSession, getSubscriptionSummary} from "../services/subscription-service";
import CancelSubscriptionModal from "../components/CancelSubscriptionModal";
import {PaymentMethodType} from "../models/enums/PaymentMethodType";
import ResumeSubscriptionModal from "../components/ResumeSubscriptionModal";
import SuccessToast from "../components/SuccessToast";

const SubscriptionSettingsPage: React.FC = () => {
    const { user: authUser, subscriptionPlan, refreshSession } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const paymentMethodStatus = searchParams.get('payment_method') ?? '';

    const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [errorToast, setErrorToast] = useState('');
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
    const [subscriptionUpdateMessage, setSubscriptionUpdateMessage] = useState('');
    const [paymentRedirecting, setPaymentRedirecting] = useState(false);
    const [successMessage, setSuccessMessage] = useState(paymentMethodStatus === 'updated' ? 'Payment method successfully updated' : '');

    const userId = authUser?.id;

    const formatMoney = (price?: PriceInfo | null) => {
        if (!price) return '';
        try {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: price.currency
            }).format(price.amount / 100) + ` / ${price.interval}`;
        } catch {
            return `${(price.amount / 100).toFixed(2)} ${price.currency} / ${price.interval}`;
        }
    };

    useEffect(() => {
        let mounted = true;

        async function load() {
            if (!userId) {
                setErrorToast('Unable to load subscription settings. Try logging in again.');
                setLoading(false);
                return;
            }

            if (subscriptionPlan?.subscriptionType === SubscriptionType.FREE) {
                const freeSummary: SubscriptionSummary = {
                    type: SubscriptionType.FREE,
                    status: StripeSubscriptionStatus.ACTIVE,
                    priceInfo: null,
                    nextBillingDate: null,
                    cancellationDate: null,
                    paymentMethodSummary: null
                };
                if (mounted) {
                    setSummary(freeSummary);
                    setLoading(false);
                }
                return;
            }

            try {
                const response = await getSubscriptionSummary(userId);
                if (!mounted) return;
                if (response.status === 200 && response.data) {
                    setSummary(response.data);
                } else {
                    setErrorToast('Unable to load subscription info. Please try again later.');
                }
            } catch (e) {
                console.error('Failed to load subscription summary', e);
                if (mounted) setErrorToast('Failed to load subscription info. Please try again later.');
            } finally {
                if (mounted) setLoading(false);
            }
        }

        load();
        return () => { mounted = false; };
    }, [userId, subscriptionPlan]);

    const handleSubscriptionCancellation = () => {
        setSubscriptionUpdateMessage('Your subscription will be cancelled at the end of the billing period. You can restart at any time!');
        refreshSession();
    }

    const handleResumeSubscription = () => {
        setSubscriptionUpdateMessage('Your subscription has been resumed!');
        refreshSession();
    }

    const handleUpdatePaymentMethod = async () => {
        try {
            setPaymentRedirecting(true);
            if(userId) {
                const response = await createStripePortalSession(userId);
                if (response.status === 200 && response.data) {
                    window.location.assign(response.data);
                } else {
                    throw new Error('No portal url');
                }
            } else {
                throw new Error('No user id');
            }
        } catch (err: any) {
            console.error('Unable to create portal session', err);
            setErrorToast('Could not open the billing portal. Please try again later.');
        } finally {
            setPaymentRedirecting(false);
        }
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <NavBar />
            <main className="flex-1">
                <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-semibold text-gray-900">Subscription Settings</h1>

                    <div className="mt-6 rounded-xl bg-white shadow-sm ring-1 ring-gray-200 p-6">
                        {subscriptionUpdateMessage && (
                            <div
                                className="mb-4 rounded-lg bg-green-50 text-green-800 ring-1 ring-green-200 p-3"
                                data-testid="subscription-update-message"
                            >
                                {subscriptionUpdateMessage}
                            </div>
                        )}
                        {loading ? (
                            <p data-testid="loading-message" className="text-gray-500">Loading subscription...</p>
                        ) : summary ? (
                            <>
                                {/* Summary content  */}
                                <div className="space-y-3" data-testid="summary-info">
                                    <div className="flex items-center gap-2">
                                        <h2 data-testid="plan-type" className="text-lg font-semibold text-gray-900">{summary.type}</h2>
                                        <span
                                            data-testid="plan-status"
                                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1
                                                ${summary.status === StripeSubscriptionStatus.ACTIVE ? 'bg-green-100 text-green-700 ring-green-200'
                                                    : summary.status === StripeSubscriptionStatus.PENDING_CANCELLATION ? 'bg-yellow-100 text-yellow-800 ring-yellow-200'
                                                    : 'bg-gray-100 text-gray-700 ring-gray-200'}`}
                                        >
                                            {summary.status.replace('_', ' ')}
                                        </span>
                                    </div>

                                    {summary.priceInfo && (
                                        <div className="text-sm">
                                            <span className="text-gray-500 block">Price</span>
                                            <span data-testid="price-info" className="font-medium">{formatMoney(summary.priceInfo)}</span>
                                        </div>
                                    )}

                                    {summary.status === StripeSubscriptionStatus.ACTIVE && summary.nextBillingDate && (
                                        <div className="text-sm">
                                            <span className="text-gray-500 block">Next billing date</span>
                                            <span data-testid="next-billing-date" className="font-medium">{summary.nextBillingDate.toLocaleDateString('en-US')}</span>
                                        </div>
                                    )}

                                    {summary.status === StripeSubscriptionStatus.PENDING_CANCELLATION && summary.cancellationDate && (
                                        <div data-testid="cancel-date" className="mt-2 rounded-lg bg-yellow-50 text-yellow-800 ring-1 ring-yellow-200 p-3">
                                            Your subscription will end on <strong>{summary.cancellationDate.toLocaleDateString('en-US')}</strong>.
                                        </div>
                                    )}

                                    {summary.paymentMethodSummary && (
                                        <div className="text-sm">
                                            <span className="text-gray-500 block">Payment method</span>
                                            {summary.paymentMethodSummary.type === PaymentMethodType.CARD &&
                                                summary.paymentMethodSummary.brand &&
                                                summary.paymentMethodSummary.last4 ? (
                                                    <span data-testid="card-payment" className="font-medium">
                                                        {summary.paymentMethodSummary.brand} •••• {summary.paymentMethodSummary.last4}
                                                    </span>
                                                ) : (
                                                <span data-testid="other-payment" className="font-medium">{summary.paymentMethodSummary.type}</span>
                                                )}
                                        </div>
                                    )}
                                </div>

                                {/* Bottom action bar */}
                                <div className="mt-6 pt-6 border-t flex flex-col items-left gap-3 sm:flex-row sm:justify-left">
                                    {summary.type !== SubscriptionType.FREE && summary.status !== StripeSubscriptionStatus.CANCELLED ? (
                                        <>
                                            {/* No need for this yet with only one paid plan */}
                                            {/*<button*/}
                                            {/*    className="inline-flex w-56 items-center justify-center rounded-lg bg-crumborange px-4 py-2*/}
                                            {/*    text-white font-medium text-center hover:opacity-90 focus-visible:outline-none*/}
                                            {/*    focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-crumborange"*/}
                                            {/*    data-testid="change-plan-btn"*/}
                                            {/*>*/}
                                            {/*    Change plan*/}
                                            {/*</button>*/}

                                            {summary.status === StripeSubscriptionStatus.PENDING_CANCELLATION && (
                                                <div className="flex flex-col items-center sm:items-end">
                                                    <button
                                                        onClick={() => setIsResumeModalOpen(true)}
                                                        className="inline-flex w-56 items-center justify-center rounded-lg bg-crumborange px-4 py-2
                                                        text-white font-medium text-center hover:opacity-90 focus-visible:outline-none
                                                        focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-crumborange"
                                                        data-testid="resume-btn"
                                                    >
                                                        Resume subscription
                                                    </button>
                                                </div>
                                            )}
                                                <div className="flex flex-col items-center sm:items-end">
                                                    <button
                                                        onClick={() => handleUpdatePaymentMethod()}
                                                        disabled={paymentRedirecting}
                                                        className="inline-flex w-56 items-center justify-center rounded-lg bg-crumborange px-4 py-2
                                                        text-white font-medium text-center hover:opacity-90 focus-visible:outline-none
                                                        focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-crumborange"
                                                        data-testid="update-payment-btn"
                                                    >
                                                        {paymentRedirecting ? 'Opening...' : 'Update payment method'}
                                                    </button>
                                                </div>
                                            {summary.status !== StripeSubscriptionStatus.PENDING_CANCELLATION && (
                                                <div className="flex flex-col items-center sm:items-end">
                                                    <button
                                                        onClick={() => setIsCancelModalOpen(true)}
                                                        className="inline-flex w-56 items-center justify-center rounded-lg border
                                                        border-red-300 px-4 py-2 text-red-700 bg-white hover:bg-red-50
                                                        disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none
                                                        focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-300"
                                                        data-testid="cancel-btn"
                                                    >
                                                        Cancel subscription
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <button
                                            className="inline-flex w-56 items-center justify-center rounded-lg bg-crumborange px-4 py-2
                                            text-white font-medium text-center hover:opacity-90 focus-visible:outline-none
                                            focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-crumborange"
                                            data-testid="upgrade-btn"
                                            onClick={() => navigate('/upgrade/subscription')}
                                        >
                                            Upgrade to Premium
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <p data-testid="no-info-message" className="text-gray-500">No subscription info available.</p>
                        )}

                    </div>
                </div>
            </main>
            <Footer />
            <ErrorToast message={errorToast} clearError={() => setErrorToast('')} />
            <SuccessToast message={successMessage} clearSuccess={() => setSuccessMessage('')} />
            <CancelSubscriptionModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                onCancel={handleSubscriptionCancellation}
                onError={(errorMessage) => setErrorToast(errorMessage)}
            />
            <ResumeSubscriptionModal
                isOpen={isResumeModalOpen}
                onClose={() => setIsResumeModalOpen(false)}
                onResume={handleResumeSubscription}
                onError={(errorMessage) => setErrorToast(errorMessage)}
            />
        </div>
    );
};

export default SubscriptionSettingsPage;